<template>
  <ak-card-header class="flex flex--center">
    <h4 class="color-grey">{{ $t('Enrolment Attribution') }}</h4>
    <div class="flex flex--end ml-auto">
      <ak-select
        v-if="!loading && !noReports"
        v-model="selectedReportName"
        :options="reportNames"
        inline-edit
        class="min-width-125 max-width-150"
        deselect-label=""
        select-label=""
        selected-label=""
      />
      <div class="flex">
        <!-- View all button -->
        <ak-tooltip
          v-if="isAllowed('enV2')"
          :placement="'left'"
          :has-arrow="false"
        >
          <template #trigger>
            <router-link :to="viewAllV2Reports">
              <ak-button circle size="small" color="transparent">
                <i class="color-primary icon-open" />
              </ak-button>
            </router-link>
          </template>
          View All
        </ak-tooltip>
        <!-- Change widget -->
        <ak-tooltip
          v-if="isOwnAccount()"
          :placement="'left'"
          :has-arrow="false"
        >
          <template #trigger>
            <a
              class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
              @click="changeWidget()"
            ></a>
          </template>
          Change Widget
        </ak-tooltip>
      </div>
    </div>
  </ak-card-header>
  <ak-card-body class="h-full min-one-whole overflow-y">
    <div v-if="!loading" class="flex min-one-whole">
      <!-- No data message -->
      <ak-empty-state
        v-if="(noReportSelected || noData) && !noReports"
        class="m-auto min-one-whole"
        icon="stats-bars"
        title="No data to display"
      />
      <ak-empty-state v-if="noReports" fa fa-light icon="file-chart-column">
        <p>
          {{ $t('Enrolment Attribution') }} allows you to see a report of the
          fluctuation in cost spent over time in all stages of your funnel.
        </p>
        <router-link :to="viewAllV2Reports">
          <ak-button color="primary" size="tiny" class="mx-auto">
            Create Report
          </ak-button>
        </router-link>
      </ak-empty-state>
      <div v-if="!noData" class="min-one-whole">
        <!-- Stats at top -->
        <div class="flex palm--hidden grid min-one-whole">
          <div class="flex__item mb max-one-quarter">
            <h4 class="color-grey mb-none">CPP</h4>
            <span>{{
              selectedReport.cpp
                ? currency + selectedReport.cpp.toFixed(2)
                : '-'
            }}</span>
          </div>
          <div class="flex__item mb max-one-quarter">
            <h4 class="color-grey mb-none">CPA</h4>
            <span>{{
              selectedReport.cpa
                ? currency + selectedReport.cpa.toFixed(2)
                : '-'
            }}</span>
          </div>
          <div class="flex__item mb max-one-quarter">
            <h4 class="color-grey mb-none">CPE</h4>
            <span>{{
              selectedReport.cpe
                ? currency + selectedReport.cpe.toFixed(2)
                : '-'
            }}</span>
          </div>
        </div>
        <!-- Highcharts -->
        <h4 class="color-grey text-center">Funnel Efficiencies by Platform</h4>
        <!-- dropdown needed for funnelPlatformFilters -->
        <div class="flex flex--center">
          <ak-select
            v-model="selectedFunnelPlatformFilterName"
            :options="funnelPlatformFilterNames"
            class="width-200 ml-auto"
            deselect-label=""
            select-label=""
            selected-label=""
          />
        </div>
        <div>
          <highcharts :options="chartOptions"></highcharts>
        </div>
      </div>
    </div>
    <!-- Loading screen -->
    <ak-loading-spinner v-else :size="'default'" class="m-auto" />
  </ak-card-body>
</template>

<script>
import { ref, onMounted, defineComponent, computed, watch } from 'vue'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import { Chart } from 'highcharts-vue'
import Highcharts from '../../../utils/highcharts.js'
import stockInit from 'highcharts/modules/stock'
import { accountObj, agencyObj, userObj } from '@akerolabs/akero-vue/src/store'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'
import { isAllowed } from '@akerolabs/akero-vue/src/services/features.js'

stockInit(Highcharts)

export default defineComponent({
  name: 'AkWidgetEnrollmentAttribution',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    highcharts: Chart,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Function}
     * @description Update the widget state
     */
    setAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Function}
     * @description Get the widget state
     */
    getAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {String}
     * @description Region/container of the widget (eg. dashRight, dashLeft)
     */
    region: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const loading = ref(true)
    const reports = ref([])
    const selectedReport = ref(null)
    const noReportSelected = ref(false)
    const noReports = ref(false)
    const noData = ref(false)
    const accountObjRef = ref({})
    const selectedFunnelPlatformFilter = ref(null)
    const platforms = ref([])
    const chartOptions = ref({})
    const lineGraphPlatformFunnelConfig = ref({})

    const currency = ref('')
    /**
     * @description Function that sets the currency based on the region
     * @param {String} region - The region to set the currency for
     * @returns {String} - The currency symbol for the region
     */
    const setCurrency = (region) => {
      switch (region) {
        case 'eu':
          return '€'
        case 'us':
          return '$'
        default:
          return '£'
      }
    }

    /**
     * @description Array of objects for the funnel platform filters
     */
    const funnelPlatformFilters = ref([
      {
        label: 'Cost Per Prospect',
        value: 'cpl',
      },
      {
        label: 'Cost Per Application',
        value: 'cpa',
      },
      {
        label: 'Cost Per Enrolment',
        value: 'cpe',
      },
    ])

    /**
     * @description Array of strings for the funnel platform filter names
     */
    const funnelPlatformFilterNames = computed(() =>
      funnelPlatformFilters.value.map((filter) => filter.label),
    )

    /**
     * @description Computed property for the selected funnel platform filter name
     */
    const selectedFunnelPlatformFilterName = computed({
      get: () =>
        selectedFunnelPlatformFilter.value
          ? selectedFunnelPlatformFilter.value.label
          : '',
      set: (value) => {
        selectedFunnelPlatformFilter.value = funnelPlatformFilters.value.find(
          (funnel) => funnel.label === value,
        )
      },
    })

    /**
     * Builds title object for highcharts
     * @param {string} titleText
     * @returns title object for highcharts
     */
    const createTitle = (titleText) => {
      return {
        text: '<span style="display: inline-block">' + titleText + '</span>',
        useHTML: true,
        margin: 15,
        style: {
          color: 'hsl(270, 86%, 60%)',
          fontSize: '0.9rem',
          zIndex: 0,
        },
      }
    }

    /**
     * Creates axis object for highcharts
     * @param {string} type
     * @param {string} titleText - axis title string
     * @param {int} tickInterval
     * @param {string} dateTimeLabelFormats
     * @returns axis object for highcharts
     */
    const createAxis = (
      type,
      titleText,
      tickInterval,
      dateTimeLabelFormats,
    ) => {
      return {
        type: type,
        tickInterval: tickInterval,
        dateTimeLabelFormats: dateTimeLabelFormats,
        title: createTitle(titleText),
      }
    }

    /**
     * @description Function that draws the Highcharts graph
     */
    const drawGraph = () => {
      /**
       * Line graph config
       */
      lineGraphPlatformFunnelConfig.value = {
        id:
          'lineGraphPlatformFunnel_' + Math.random().toString(36).substring(7),
        yAxisTitle: selectedFunnelPlatformFilter.value.label,
        xAxisTitle: 'Date',
        tooltipFormatter: () => {
          return [
            '<b>' + moment(this.x).format('MMM YYYY') + '</b><br/>',
          ].concat(
            this.points
              ? this.points.map((point) => {
                  return (
                    '<b>' +
                    point.series.name +
                    ': </b>' +
                    currency.value +
                    point.y.toLocaleString('en-GB') +
                    '<br/>'
                  )
                })
              : [],
          )
        },
      }
      chartOptions.value = {
        title: '',
        chart: {
          events: {
            /**
             * @description Redraws the graph when the window is resized.
             */
            load: () => {
              const chart = this
              window.addEventListener('resize', () => {
                chart.reflow()
              })
            },
          },
        },
        labels: {
          items: [
            {
              style: {
                left: '50px',
                top: '18px',
                color:
                  (Highcharts.theme && Highcharts.theme.textColor) || 'black',
              },
            },
          ],
        },
        yAxis: createAxis(
          null,
          selectedFunnelPlatformFilter.value.label,
          null,
          null,
        ),
        xAxis: createAxis('datetime', 'Date', 24 * 3600 * 1000 * 30, {
          year: '%b %Y',
          month: '%b %Y',
          week: '%e %b %Y',
        }),
        loading: {
          style: {
            opacity: 1,
          },
        },
        legend: {
          layout: 'vertical',
          verticalAlign: 'top',
          x: -30,
          y: 30,
          floating: true,
          backgroundColor: Highcharts.defaultOptions.chart.backgroundColor,
        },
        series: {
          events: {
            legendItemClick: () => {
              return false
            },
          },
        },
      }
      const chartData = platforms.value.map((platform) => {
        let name = platform[0].toUpperCase() + platform.substring(1)
        let platformData = selectedReport.value.data.funnelPlatformData
          .filter((row) => {
            return row.platform_grouped === platform
          })
          .map((row) => {
            let obj = {
              x: Date.parse(row.month_start.value),
              name: name,
            }
            let selectedFilterValue = selectedFunnelPlatformFilter.value.value
            if (['cpl', 'cpa', 'cpe'].includes(selectedFilterValue)) {
              obj.y = parseFloat(row[selectedFilterValue])
            }
            return obj
          })
        return {
          data: platformData,
          name: name,
        }
      })
      chartOptions.value.series = chartData
    }

    const viewAllV2Reports = computed(() => {
      return { name: 'enrolmentattribution.manager.v2.main' }
    })

    const reportNames = computed(() =>
      reports.value.map((report) => report.title),
    )

    /**
     * @description Computed property that gets and sets the name of the selected report
     * @type {ComputedRef<string>}
     */
    const selectedReportName = computed({
      get: () => (selectedReport.value ? selectedReport.value.title : ''),
      set: (value) => {
        selectedReport.value = reports.value.find(
          (report) => report.title === value,
        )
      },
    })

    /**
     * @description Watch for changes in the selected report and update the widget state accordingly
     * @param {Object} newVal - The new value of the selected report
     */
    watch(selectedReport, (newVal) => {
      if (!newVal) {
        noReportSelected.value = true
        noData.value = true
        removeState()
        return
      }
      noReportSelected.value = false
      setState(newVal)
      reportSelected(newVal)
    })

    /**
     * @description Resets platforms and gets new ones from data
     */
    const getPlatforms = () => {
      platforms.value = []
      for (const data of selectedReport.value.data.overallPlatformData) {
        const platform = data.platform_grouped
        if (!platforms.value.includes(platform)) {
          platforms.value.push(platform)
        }
      }
    }

    /**
     * @description Get reports from api
     * @async
     */
    const getReports = async () => {
      try {
        const { data } = await request.get(
          '/enrolment_attribution/v2',
          reportsRequest.value,
        )
        // Don't include total report.
        reports.value = data.data.filter((report) => report.title !== 'Total')
      } catch {
        ToastProgrammatic.open({
          message: 'Error getting reports',
          type: 'is-danger',
        })
      }
    }

    /**
     * @description request params for V2 Reports
     */
    const reportsRequest = computed(() => ({
      params: {
        account: accountObj().id,
        agency: agencyObj().id,
        populate: 'campaign.id',
        per_page: 0,
      },
    }))

    /**
     * @description Get report data for the selected report
     * @async
     */
    const getReportsData = async () => {
      loading.value = true
      try {
        const { data } = await request.get(
          '/enrolment_attribution/v2/dashboard',
          reportsDataRequest.value,
        )
        selectedReport.value.data = data.data
      } catch {
        ToastProgrammatic.open({
          message: 'Error getting reports',
          type: 'is-danger',
        })
      }
    }

    /**
     * @description request params for V2 Reports data
     */
    const reportsDataRequest = computed(() => ({
      params: {
        account: accountObj().id,
        agency: agencyObj().id,
        reportIds: selectedReport.value.id,
      },
    }))

    /**
     * @description This function is called when a report is selected. It fetches the report data, gets the platforms, and draws the graph if data is available.
     * @async
     */
    const reportSelected = async () => {
      await getReportsData()
      getPlatforms()

      const { cpa, cpe, cpp } = selectedReport.value

      if (
        selectedReport.value.data.length < 0 ||
        ((!cpa || cpa === 0) && (!cpe || cpe === 0) && (!cpp || cpp === 0))
      ) {
        noData.value = true
        loading.value = false
      } else {
        drawGraph()
        loading.value = false
        noData.value = false
      }
    }

    /**
     * @param {object} report
     * @description Set the state of the widget region
     */
    const setState = (report) => {
      props.setAccountState(`widgets.data.${props.region}._id`, report.id)
    }

    /**
     * @description Remove the state of the widget region
     */
    const removeState = () => {
      props.setAccountState(`widgets.data.${props.region}._id`, null)
    }

    /**
     * @description Check for a state in the widget region
     * If no state is found, set default values
     * If a state is found, set the selected form to the state
     */
    const checkForState = () => {
      const desiredReport = props.getAccountState(
        'widgets.data.' + props.region,
      )
      if (!desiredReport || !desiredReport._id || desiredReport._id === '') {
        noData.value = true
        noReportSelected.value = true
        loading.value = false
        return
      } else {
        selectedReport.value = reports.value.find(
          (report) => report.id === desiredReport._id,
        )
      }
    }

    /**
     * @description Called after mount to build the widget
     */
    const init = async () => {
      loading.value = true

      // Use locales for enrollment/enrolment spelling in dropdown
      if (userObj().profile.language === 'en-US') {
        funnelPlatformFilters.value[2].label = 'Cost Per Enrollment'
      }

      // Set the default selected filter to be "Cost Per Prospect"
      selectedFunnelPlatformFilter.value = funnelPlatformFilters.value[0]

      /**
       * @description Watch for changes in the selected funnel platform filter.
       * Update the graph accordingly
       */
      watch(selectedFunnelPlatformFilter, () => {
        drawGraph()
      })

      // Get account Obj and set correct currency.
      accountObjRef.value = accountObj() // ensure query only called once
      currency.value = setCurrency(accountObj().contactDetails.address.country)

      // Get account v2 reports. Check for no reports.
      await getReports()
      if (reports.value.length < 1) {
        noReports.value = true
        noData.value = true
        loading.value = false
        return
      }

      // Check for widget state. Will show no data if no report selected previously.
      checkForState()
    }

    onMounted(() => {
      init()
    })
    return {
      isOwnAccount,
      isAllowed,
      loading,
      noData,
      noReportSelected,
      noReports,
      reports,
      selectedReport,
      reportNames,
      selectedReportName,
      viewAllV2Reports,
      currency,
      chartOptions,
      funnelPlatformFilters,
      funnelPlatformFilterNames,
      selectedFunnelPlatformFilterName,
    }
  },
})
</script>

<i18n>
  {
    "en" : {
      "Enrolment Attribution": "Enrolment Attribution",
      "Enrolment": "Enrolment",
    },
    "en-US": {
      "Enrolment Attribution": "Enrollment Attribution",
      "Enrolment": "Enrollment",
    }
  }
</i18n>
